import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateTaskDialog } from '@/components/CreateTaskDialog';
import { useAuth } from './AuthContext';

const CreateTaskContext = createContext(null);

export const CreateTaskProvider = ({ children }) => {
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    
    // Dialog state
    const [isOpen, setIsOpen] = useState(false);
    const [allProjects, setAllProjects] = useState([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    
    // Prefill data
    const [selectedProject, setSelectedProject] = useState(null);
    const [initialData, setInitialData] = useState(null);
    const [generatingTask, setGeneratingTask] = useState(false);
    const [createdFromSubmission, setCreatedFromSubmission] = useState(null);
    const [onSuccessCallback, setOnSuccessCallback] = useState(null);

    // Fetch all projects (cached)
    const fetchProjects = useCallback(async () => {
        if (allProjects.length > 0) return; // Already cached
        
        setIsLoadingProjects(true);
        try {
            const response = await axiosInstance.get('/projects');
            setAllProjects(response.data);
        } catch (error) {
            console.error('Error fetching projects:', error);
            setAllProjects([]);
        } finally {
            setIsLoadingProjects(false);
        }
    }, [axiosInstance]);

    // Prefetch projects on mount for instant dialog opening
    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    // Open dialog with optional prefill data
    const openCreateTaskDialog = useCallback((options = {}) => {
        const {
            project,
            summary,
            description,
            status,
            assigneeId,
            priority,
            labels,
            releaseId,
            scope,
            submissionFiles,
            generatingTask: isGenerating = false,
            createdFromSubmission: submissionData = null,
            onSuccess
        } = options;

        // Set prefill data - create new object to trigger React updates
        setSelectedProject(project || null);
        setInitialData({
            summary,
            description,
            status,
            assigneeId,
            priority,
            labels,
            releaseId,
            scope,
            submissionFiles,
            // Add timestamp to force new object reference
            _timestamp: Date.now()
        });
        setGeneratingTask(isGenerating);
        setCreatedFromSubmission(submissionData);
        
        // Store success callback (wrap in function to avoid direct state issues)
        if (onSuccess) {
            setOnSuccessCallback(() => onSuccess);
        } else {
            setOnSuccessCallback(null);
        }

        // Ensure projects are loaded
        if (allProjects.length === 0) {
            fetchProjects();
        }

        setIsOpen(true);
    }, [allProjects.length, fetchProjects]);

    // Close dialog and reset state
    const closeCreateTaskDialog = useCallback(() => {
        setIsOpen(false);
        // Small delay to reset state for smooth animation
        setTimeout(() => {
            setSelectedProject(null);
            setInitialData(null);
            setGeneratingTask(false);
            setCreatedFromSubmission(null);
            setOnSuccessCallback(null);
        }, 200);
    }, []);

    // Handle task creation
    const handleTaskCreated = useCallback((task) => {
        // Call custom success callback if provided
        if (onSuccessCallback) {
            onSuccessCallback(task);
        } else {
            // Default behavior: navigate to the task
            if (task && task.key && task.organization?.slug) {
                navigate(`/${task.organization.slug}/tasks/${task.key}`);
            }
        }
        
        closeCreateTaskDialog();
    }, [onSuccessCallback, navigate, closeCreateTaskDialog]);

    const value = {
        isOpen,
        openCreateTaskDialog,
        closeCreateTaskDialog,
    };

    return (
        <CreateTaskContext.Provider value={value}>
            {children}
            <CreateTaskDialog
                open={isOpen}
                onOpenChange={closeCreateTaskDialog}
                project={selectedProject}
                projects={allProjects}
                initialData={initialData}
                generatingTask={generatingTask}
                createdFromSubmission={createdFromSubmission}
                onTaskCreated={handleTaskCreated}
            />
        </CreateTaskContext.Provider>
    );
};

export const useCreateTask = () => {
    const context = useContext(CreateTaskContext);
    if (!context) {
        throw new Error('useCreateTask must be used within CreateTaskProvider');
    }
    return context;
};

